import React, { useEffect, useRef, useState } from 'react';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form, Modal } from 'semantic-ui-react';
import cn from 'classnames';
import Select from 'react-select';
import Validation from '../../Validation/Validation';
import { vehicleValidations } from '../../../validations/vehicleValidations';
import showErrorNotification from '../../../helpers/errorHandler';

import commonStyles from '../../FormComponents/formGroupsCommon.module.scss';
import styles from './VehicleModal.module.scss';

const VehicleModal = (props) => {
  const [open, setOpen] = React.useState(false);
  const form = useRef();

  const {
    vehicle,
    manufacturers,
    trigger,
    modalHeaderText,
    clearError,
    submitHandler,
  } = props;

  const { t } = useTranslation();

  const [curManufacturer, setCurManufacturer] = useState();

  const {
    handleSubmit,
    register,
    unregister,
    errors,
    setValue,
    reset,
  } = useForm({
    validationSchema: vehicleValidations,
    mode: 'onBlur',
  });

  useEffect(() => {
    register({ name: 'id' });
    register({ name: 'manufacturer' });
    register({ name: 'name' });
    return () => {
      unregister('id');
      unregister('manufacturer');
      unregister('name');
    };
  }, [register, unregister]);

  useEffect(() => {
    if (vehicle) {
      setValue('id', vehicle.modelId);
      setValue('manufacturer', vehicle.manufacturerId);
      setValue('name', vehicle.modelName);
      setCurManufacturer(vehicle && manufacturers
        && manufacturers.find((man) => man.id === vehicle.manufacturerId));
    } else {
      reset();
      setValue('manufacturer', '');
      setValue('name', '');
      setCurManufacturer(null);
    }
  }, [vehicle, open, setValue, manufacturers, reset]);

  const handleManufacturerChange = (manufacturer) => {
    if (manufacturer) {
      setCurManufacturer(manufacturer);
      setValue('manufacturer', manufacturer.id, true);
    }
  };

  return (
    <Modal
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      closeOnEscape
      trigger={trigger}
      size="tiny"
    >

      <Modal.Header>{modalHeaderText}</Modal.Header>
      <Modal.Content className={styles.formGroupContainer}>
        <Form
          ref={form}
          onSubmit={handleSubmit((data) => {
            submitHandler(data).then(() => {
              setOpen(false);
            }).catch((error) => {
              showErrorNotification(error, 'VEHICLE_ERROR');
            });
          })}
          className={commonStyles.userForm}
        >
          <Form.Group className={commonStyles.formGroup}>
            <Form.Field width={16}>
              <label className={commonStyles.fieldLabel}>
                {t('vehicleModal.manufacturerName')}
              </label>

              <Validation
                showMessage
                name="manufacturer"
                errors={errors}
                clearError={clearError}
              >
                <Select
                  value={curManufacturer}
                  className={styles.optionSelect}
                  options={manufacturers}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  onChange={(v) => handleManufacturerChange(v)}
                  placeholder={t('select')}
                />

              </Validation>
            </Form.Field>
          </Form.Group>
          <Form.Group className={commonStyles.formGroup}>
            <Form.Field width={16}>
              <label className={commonStyles.fieldLabel}>
                {t('vehicleModal.modelName')}
              </label>
              <Validation
                name="name"
                showMessage
                errors={errors}
                clearError={clearError}
              >
                <input
                  defaultValue={vehicle ? vehicle.modelName : ''}
                  name="name"
                  onChange={(e) => setValue('name', e.target.value.trim(), true)}
                />
              </Validation>
            </Form.Field>
          </Form.Group>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <div className={styles.modalButtonContainer}>
          <button
            type="button"
            className={cn(styles.modalButton, styles.cancel)}
            onClick={() => setOpen(false)}
          >
            {t('leaveWarningModal.cancel')}
          </button>
          <button
            type="button"
            className={cn('orange_button', styles.modalButton)}
            onClick={() => {
              form.current.handleSubmit();
            }}
          >
            {t('leaveWarningModal.confirm')}
          </button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};

VehicleModal.propTypes = {
  vehicle: PropTypes.shape({
    modelId: PropTypes.number,
    manufacturerId: PropTypes.number,
    modelName: PropTypes.string,
  }),
  manufacturers: PropTypes.arrayOf(PropTypes.shape({})),
  trigger: PropTypes.element,
  modalHeaderText: PropTypes.string,
  clearError: PropTypes.func,
  submitHandler: PropTypes.func.isRequired,
};

VehicleModal.defaultProps = {
  vehicle: null,
  manufacturers: [],
  trigger: null,
  modalHeaderText: '',
  clearError: () => {},
};

export default VehicleModal;
